/* eslint-disable max-lines */
import { EVENT_TYPES } from 'src/interfaces/enums';
import { IPlayerError } from 'src/interfaces/PlayerError';
import { v4 as uuidv4 } from 'uuid';
import { IPlayerGateway } from 'src/core/player/interfaces/PlayerGateway';

class Player {
  protected _viewId: string;
  protected _playerInitTime = 0;
  constructor(protected gateway: IPlayerGateway) {
    this.gateway = gateway;
    this._viewId = uuidv4();
    this._playerInitTime = Date.now();
  }

  get playerInitTime() {
    return this._playerInitTime;
  }

  get viewId() {
    return this._viewId;
  }

  get duration() {
    return this.gateway.duration;
  }

  get currentTime() {
    return this.gateway.currentTime;
  }

  get percentWatched() {
    return (this.currentTime * 100) / this.duration;
  }

  get watchTimes() {
    return this.gateway.watchTimes;
  }

  get lastIntervalWatched() {
    return this.gateway.lastIntervalWatched;
  }

  get hasEnded(): boolean {
    return this.gateway.hasEnded;
  }

  get volumePercent(): number {
    return Math.round(this.volume() * 100);
  }

  get isMuted(): boolean {
    return this.gateway.isMuted;
  }

  get isAudio() {
    return this.gateway.isAudio;
  }

  get userActive() {
    return this.gateway.userActive;
  }

  get videoDimensions() {
    return this.gateway.videoDimensions;
  }

  get playbackRate() {
    return this.gateway.playbackRate;
  }

  get bufferIndicatorPercent() {
    const totalDuration = this.duration;
    const buffered = this.buffered;
    const currentTime = this.currentTime;

    for (let i = 0; i < buffered.length; i++) {
      const position = buffered.end(i);
      if (position > currentTime) return (position * 100) / totalDuration;
    }

    return 0;
  }

  get qualityLevels() {
    return this.gateway.qualityLevels;
  }

  get currentPlaylist() {
    return this.gateway.currentPlaylist;
  }

  get playlists() {
    return this.gateway.playlists;
  }

  get textTracks() {
    return this.gateway.textTracks;
  }

  get buffered() {
    return this.gateway.buffered;
  }

  get videoPlaybackQuality() {
    return this.gateway.videoPlaybackQuality;
  }

  get hasSupportToHLS() {
    return this.gateway.hasSupportToHLS;
  }

  get hasSupportToNativeHLS() {
    return this.gateway.hasSupportToNativeHLS;
  }

  get mediaInfo() {
    return this.gateway.mediaInfo;
  }

  play() {
    return this.gateway.play();
  }

  pause() {
    this.gateway.pause();
  }

  paused() {
    return this.gateway.paused();
  }

  hasStarted() {
    return this.gateway.hasStarted();
  }

  togglePlay() {
    this.paused() ? this.play() : this.pause();
  }

  setFirstPlayTime(time: number) {
    this.play();
    this.setCurrentTime(time);
    this.pause();
  }

  setCurrentTimePercent(percent: number) {
    if (percent < 0 || percent > 100) return;

    this.setCurrentTime((this.duration / 100) * percent);
  }

  setVolume(volume: number) {
    this.gateway.setVolume(volume);
  }

  volume() {
    return this.gateway.volume();
  }

  muted() {
    this.gateway.muted();
  }

  unmuted() {
    this.gateway.unmuted();
  }

  setUserActive(active: boolean) {
    this.gateway.setUserActive(active);
  }

  togglePictureInPicture() {
    this.gateway.togglePictureInPicture();
  }

  setPlaybackRate(rate: number) {
    this.gateway.setPlaybackRate(rate);
  }

  clean() {
    this.gateway.clean();
  }

  setCustomErrorMessages(errors: { [code: string]: IPlayerError }) {
    this.gateway.setCustomErrorMessages(errors);
  }

  setCurrentTime(seconds: number) {
    this.gateway.setCurrentTime(seconds);
  }

  setVolumePercent(percent: number) {
    if (this.isMuted && percent > 0) this.unmuted();
    this.setVolume(percent / 100);
  }

  startMutedPlay() {
    if (!this.paused()) return;
    this.muted();
    this.setVolumePercent(0);
    this.play();
  }

  async autoPlay() {
    try {
      await this.play();
    } catch (error) {
      this.startMutedPlay();
    }
  }

  addSubtitleListener(callback: () => void) {
    this.gateway.addSubtitleListener(callback);
  }

  addEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.gateway.addEventListener(eventType, callback);
  }

  removeEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.gateway.removeEventListener(eventType, callback);
  }

  addFirstEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.gateway.addFirstEventListener(eventType, callback);
  }

  addEventListeners(eventTypes: EVENT_TYPES[], callback: (...args: unknown[]) => void) {
    eventTypes.forEach((eventType) => this.addEventListener(eventType, callback));
  }

  triggerEvent(eventType: EVENT_TYPES, ...args: unknown[]) {
    this.gateway.triggerEvent(eventType, args);
  }

  el() {
    return this.gateway.el();
  }

  getErrorMessage() {
    return this.gateway.getErrorMessage();
  }

  setResolution(currentResolution: string) {
    this.gateway.setResolution(currentResolution);
  }

  activateSubtitle(active: boolean) {
    this.gateway.activateSubtitle(active);
  }

  setupBeforeRequest(withCredentials: boolean) {
    this.gateway.setupBeforeRequest(withCredentials);
  }

  enableDRM() {
    return this.gateway.enableDRM();
  }

  initializeMediaKeys() {
    this.gateway.initializeMediaKeys();
  }

  disableLoader() {
    this.gateway.disableLoader();
  }

  readyState() {
    return this.gateway.readyState();
  }
}

export default Player;
